import React, { useState } from "react";
import { Siren, Loader2, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import { sosApi, errorMessage } from "../../lib/api";
import { CONTACT } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

// Floating one-tap emergency button for customers & drivers. Captures live GPS and
// alerts the response team. Shown app-wide for rider/driver roles via AppLayout.
export default function SosButton({ variant = "fab" }) {
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [supportPhone, setSupportPhone] = useState(CONTACT.phone);

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
      );
    });

  const send = async () => {
    setSending(true);
    try {
      const pos = await getPosition();
      if (!pos) {
        toast.error("Couldn't get your location. Please enable location access and try again.");
        setSending(false);
        return;
      }
      const res = await sosApi.raise({ lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });
      if (res?.support_phone) setSupportPhone(res.support_phone);
      setDone(true);
      toast.success("Help is on the way. Our team has been alerted.");
    } catch (e) {
      toast.error(errorMessage(e, "Could not send SOS. Please call emergency services."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          aria-label="SOS/Emergency"
          title="SOS / Emergency"
          data-testid="sos-fab"
          className="flex h-9 items-center gap-1.5 rounded-full bg-red-600 px-3 text-xs font-bold text-white transition-transform hover:scale-105 active:scale-95"
        >
          <Siren className="h-4 w-4" />
          <span>Emergency</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setConfirm(true)}
          aria-label="SOS/Emergency"
          data-testid="sos-fab"
          className="fixed bottom-24 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-600/40 transition-transform hover:scale-105 active:scale-95 md:bottom-8"
        >
          <Siren className="h-6 w-6" />
        </button>
      )}

      <Dialog open={confirm} onOpenChange={(o) => !sending && setConfirm(o)}>
        <DialogContent className="z-[70] max-w-sm" data-testid="sos-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Siren className="h-5 w-5" /> Emergency SOS
            </DialogTitle>
            <DialogDescription>
              This sends your live location and trip details to our safety team immediately.
              Use only in a real emergency.
            </DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="sos-sent">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700"><Check className="h-6 w-6" /></div>
              <p className="text-sm font-semibold text-ink">Alert sent. Help is on the way.</p>
              <a href={`tel:${String(supportPhone).replace(/\s+/g, "")}`} data-testid="sos-call" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700">
                <Phone className="h-4 w-4" /> Call us now ({supportPhone})
              </a>
              <button type="button" className="text-xs font-semibold text-ink-muted" onClick={() => { setConfirm(false); setDone(false); }} data-testid="sos-close">Close</button>
            </div>
          ) : (
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setConfirm(false)} disabled={sending} data-testid="sos-cancel">Cancel</Button>
              <Button className="rounded-full bg-red-600 text-white hover:bg-red-700" onClick={send} disabled={sending} data-testid="sos-send">
                {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Send SOS now"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
